import React, { useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Table, Button, Modal, Row, notification, Input, Select, Col, Tooltip } from 'antd';
import { ArrowLeftOutlined, DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { config } from '../../config';
import { formatFileSize, SEARCH_IN_DOCUMENTS_QUERY } from './DataFilesConstants';
import CustomTooltip from '../CustomComponents/CustomTooltip';

const Option = Select;

const ContentSearch = () => {
  const navigate = useNavigate();
  const [filesToBeDownloaded, setFilesToBeDownloaded] = useState([]);
  const [visible, setVisible] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [search1, setSearch1] = useState<string>('');
  const [search2, setSearch2] = useState<string>('');
  const [operator, setOperator] = useState<'AND' | 'OR' | 'None'>('None');
  const [downloading, setDownloading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const searchTitle = (
    <ul style={{ paddingLeft: '20px', margin: '0 10px', listStyleType: 'disc' }}>
      <li style={{ textAlign: 'justify' }}>
        Search functionality only searches within documents, not file names or other information.
        Please enter only one word in the search field. The dropdown box next to the search field
        allows for the addition of the second word. Select AND in the dropdown box to get results
        that contain both keywords. Selecting OR will get results that contain either keyword.
        Alternatively, selecting NONE will search a single word. Partial words can also be searched.
        For example, &quot;var&quot; will return documents containing the words &quot;vary&quot;,
        &quot;variation&quot;, &quot;various&quot;, etc.
      </li>
      <br />
      <li style={{ textAlign: 'justify' }}>
        The search for more generic terms (e.g. motion, notice, AER) may return hundreds of
        documents and may take a longer period of time to load. It is recommended that search words
        be more specific in order to get better results, but if that is not possible, please be
        patient with the load times.
      </li>
    </ul>
  );
  const previewDocument = (url: string) => {
    setPdfUrl(url);
    setVisible(true);
  };

  const closeModal = () => {
    setVisible(false);
    setPdfUrl(null);
  };
  const [searchDocumentByContext, { data: proceedingsData, loading }] =
    useLazyQuery(SEARCH_IN_DOCUMENTS_QUERY);
  const addToDownloadList = (
    selected: boolean,
    proceedingNumber: string,
    exhibitId: string,
    size: number
  ) => {
    if (selected) {
      filesToBeDownloaded.push({ fileName: `${proceedingNumber}/${exhibitId}`, size });
      setFilesToBeDownloaded(filesToBeDownloaded);
    } else {
      const newFileList = filesToBeDownloaded.filter(
        (file) => file.fileName !== `${proceedingNumber}/${exhibitId}`
      );
      setFilesToBeDownloaded(newFileList);
    }
  };
  const downloadFiles = async () => {
    if (filesToBeDownloaded.length < 2) {
      notification.info({
        message:
          'Batch download requires more than 1 exhibit to be selected. Please click on the File Name to download a single exhibit.',
      });
    } else {
      try {
        const fileNames: string[] = [];
        let totalSize = 0;
        filesToBeDownloaded.forEach((file) => {
          fileNames.push(file.fileName);
          totalSize += Number(file.size);
        });
        if (totalSize > 3221225472) {
          notification.error({
            message:
              totalSize +
              'Total size of all selected files exceeds the maximum limit of 3 GB. Please refer to the file sizes and change your selection.',
          });
        } else {
          setDownloading(true);
          const response = await axios.post(
            `${config.REACT_APP_SERVICE_URI}/file/downloadBulk`,
            { fileNames: fileNames },
            {
              headers: {
                Authorization: localStorage.getItem('accessToken'),
              },
              responseType: 'blob',
            }
          );

          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'proceedingExhibits.zip');
          document.body.appendChild(link);
          link.click();
          link.remove();
          setFilesToBeDownloaded([]);
          setSelectedRowKeys([]);
          setDownloading(false);
        }
      } catch (error) {
        console.error('Error downloading the files:', error);
      }
    }
  };
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys: any, selectedRows: any) => {
      setSelectedRowKeys(selectedRowKeys);
    },
    onSelect: (record: any, selected: boolean, selectedRows: any) => {
      addToDownloadList(selected, record.proceedingNumber, record.id, record.size);
    },
    onSelectAll: (selected: any, selectedRows: any, changeRows: any) => {
      if (!selected) {
        setSelectedRowKeys([]);
        setFilesToBeDownloaded([]);
      } else {
        setSelectedRowKeys(selectedRows.map((row: any) => row.key));
        selectedRows.map((row: { id: string; proceedingNumber: string; size: number }) =>
          addToDownloadList(selected, row.proceedingNumber, row.id, row.size)
        );
      }
    },
  };
  const allTypes = Array.from(
    new Set(proceedingsData?.searchDocument?.data?.flatMap((file: any) => file.type))
  ).map((fileType) => ({ text: fileType, value: fileType }));
  const allProceedings = Array.from(
    new Set(proceedingsData?.searchDocument?.data?.flatMap((file: any) => file.proceedingNumber))
  ).map((proceedingNumber) => ({ text: proceedingNumber, value: proceedingNumber }));
  const major = (exhibitNumber: string) => exhibitNumber?.split('.')[0];
  const minor = (exhibitNumber: string) => exhibitNumber?.split('.')[1];
  const columns: any[] = [
    {
      title: 'Proceeding Id',
      dataIndex: 'proceedingNumber',
      key: 'proceedingNumber',
      width: 120,
      sorter: (a: any, b: any) => Number(a.proceedingNumber) - Number(b.proceedingNumber),
      filters: allProceedings,
      onFilter: (value: any, record: any) => {
        return record.proceedingNumber.toString().indexOf(value) !== -1;
      },
    },
    {
      title: 'Exhibit Number',
      dataIndex: 'exhibitNumber',
      key: 'exhibitNumber',
      width: 110,
      render: (_: any, record: any) => {
        return record.originalExhibitFromLegacy
          ? record.originalExhibitFromLegacy
          : record.exhibitNumber;
      },
      sorter: (a: any, b: any) =>
        Number(major(a.exhibitNumber)) - Number(major(b.exhibitNumber)) ||
        Number(minor(a.exhibitNumber)) - Number(minor(b.exhibitNumber)),
    },
    {
      width: 250,
      title: 'File Type',
      dataIndex: 'type',
      filters: allTypes,
      onFilter: (value: any, record: any) => {
        return record.type.toString().indexOf(value) !== -1;
      },
      render: (types: string[]) => Array.isArray(types) && types?.join(', '),
    },
    {
      title: 'File Name',
      dataIndex: 'fileName',
      key: 'fileName',
      width: 110,
      onCell: () => {
        return {
          onClick: (event: any) => {
            event.stopPropagation();
          },
        };
      },
      render: (fileName: string, record: any) => {
        const filePath = `${record.proceedingNumber}/${record.id}`;
        const fileUrl = `${config.REACT_APP_SERVICE_URI}/file/download?attachmentId=${filePath}`;
        return (
          <Button
            style={{ textWrap: 'wrap', height: 'auto', textAlign: 'left' }}
            type="link"
            onClick={() =>
              window.open(
                `${fileUrl}&authorization=${localStorage.getItem('accessToken')}`,
                '_blank'
              )
            }
            icon={<DownloadOutlined />}
          >
            {fileName}
          </Button>
        );
      },
    },
    {
      title: 'Preview',
      width: 180,
      dataIndex: 'fileName',
      key: 'name',
      onCell: () => {
        return {
          onClick: (event: any) => {
            event.stopPropagation();
          },
        };
      },
      render: (fileName: string, record: any) => {
        const filePath = `${record.proceedingNumber}/${record.id}`;
        const link = `${config.REACT_APP_SERVICE_URI}/file/download?attachmentId=${filePath}&authorization=${localStorage.getItem('accessToken')}`;
        const extension = fileName?.split('.').pop();
        return extension?.toLowerCase() === 'pdf' ? (
          <Button color="default" onClick={() => previewDocument(link)} icon={<EyeOutlined />}>
            Preview
          </Button>
        ) : (
          ''
        );
      },
    },
    {
      width: 50,
      title: 'File Size',
      dataIndex: 'size',
      render: (s: any) => {
        return formatFileSize(s);
      },
    },
  ];
  const handleChange = (value: any) => {
    if (!search1) {
      notification.info({ message: 'First Keyword can not be empty!' });
    } else {
      setOperator(value);
      if (value === 'None') {
        setSearch2('');
      }
    }
  };
  const handleTableChange = () => {
    setSelectedRowKeys([]);
    setFilesToBeDownloaded([]);
  };
  const handleSearch = () => {
    searchDocumentByContext({
      variables: {
        input: { keyword: [search1, search2], operator: operator },
      },
    });
  };
  return (
    <>
      <Row style={{ marginTop: '10px', marginBottom: '10px' }}>
        <div style={{ marginTop: '10px' }}>
          <span className="highlighted-title">Search By Document Content</span>
        </div>
        <Link to="/hearing">
          <Button
            type="primary"
            icon={<ArrowLeftOutlined />}
            style={{ marginLeft: '8px', height: '40px', display: 'flex', alignItems: 'center' }}
          >
            Go To Proceeding List
          </Button>
        </Link>
      </Row>
      <Row gutter={[16, 16]} style={{ padding: '10px ' }}>
        <Col>
          <Input
            placeholder="Enter first search keyword"
            allowClear
            value={search1}
            onChange={(e) => {
              if (!e.target.value) {
                setOperator('None');
              }
              setSearch1(e.target.value);
            }}
            onPressEnter={handleSearch}
            style={{ width: 300 }}
          />
        </Col>
        <Col>
          <Select
            defaultValue="None"
            value={operator}
            onChange={(value: 'AND' | 'OR' | 'None') => handleChange(value)}
            style={{ width: 100 }}
          >
            <Option value="AND">AND</Option>
            <Option value="OR">OR</Option>
            <Option value="None">None</Option>
          </Select>
        </Col>
        {operator !== 'None' && (
          <Col>
            <Input
              placeholder="Enter second search keyword"
              allowClear
              value={search2}
              onChange={(e) => setSearch2(e.target.value)}
              onPressEnter={handleSearch}
              style={{ width: 300 }}
            />
          </Col>
        )}
        <Col>
          <Button type="primary" onClick={handleSearch} style={{ marginLeft: '10px' }}>
            Search by Keyword{operator !== 'None' ? 's' : ''}
          </Button>
          <CustomTooltip title={searchTitle}></CustomTooltip>
        </Col>
      </Row>
      <Row style={{ marginTop: '10px' }}>
        <span className="highlighted-title" style={{ marginRight: '10px', justifySelf: 'center' }}>
          Proceeding List
        </span>
        <Button type="primary" onClick={async () => await downloadFiles()}>
          Download Selected
        </Button>
      </Row>
      <Table
        dataSource={proceedingsData?.searchDocument?.data}
        loading={loading || downloading}
        columns={columns}
        rowSelection={{ ...rowSelection }}
        onChange={handleTableChange}
        rowClassName="clickable-row"
        onRow={(record: any) => ({
          onClick: () => {
            navigate(`/hearing/proceeding/${record.proceedingNumber}`);
          },
          style: { cursor: 'pointer', verticalAlign: 'top' },
        })}
      />
      <Modal open={visible} onCancel={closeModal} footer={null} width={800}>
        {pdfUrl && <Viewer fileUrl={pdfUrl}></Viewer>}
      </Modal>
    </>
  );
};

export default ContentSearch;
